import React from 'react';
import { GenericModal, ModalButton } from '../GenericModal';
import styles from './SessionTimeoutModal.module.css';

/**
 * SessionTimeoutModal Props Interface
 */
export interface SessionTimeoutModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when user clicks login button */
  onLogin: () => void;
}

/**
 * SessionTimeoutModal - Specialized modal for session expiration
 * 
 * Features:
 * - Displays when user's session has expired (403/401 errors)
 * - Security-focused design with lock icon
 * - Single action button to redirect to login
 * - Non-dismissable (no close button, no backdrop close)
 * - Dark futuristic styling
 */
export const SessionTimeoutModal: React.FC<SessionTimeoutModalProps> = ({
  isOpen,
  onLogin,
}) => {
  // Security lock icon
  const securityIcon = (
    <div className={styles.securityIconWrapper}>
      <svg 
        className={styles.lockIcon} 
        viewBox="0 0 24 24" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 2C9.243 2 7 4.243 7 7v3H6c-1.103 0-2 .897-2 2v8c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2v-8c0-1.103-.897-2-2-2h-1V7c0-2.757-2.243-5-5-5zm0 2c1.654 0 3 1.346 3 3v3H9V7c0-1.654 1.346-3 3-3z"
          fill="currentColor"
        />
      </svg>
    </div>
  );

  // Button configuration
  const buttons: ModalButton[] = [
    {
      label: 'Login Again',
      onClick: onLogin,
      variant: 'primary',
    },
  ];

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={() => {}} // No-op: modal is non-dismissable
      title="Session Expired"
      variant="warning"
      buttons={buttons}
      showCloseButton={false}
      disableBackdropClose={true}
      icon={securityIcon}
    >
      <div className={styles.content}>
        <p className={styles.message}>
          Your session has expired for security reasons. Please log in again to continue.
        </p>
        <div className={styles.securityNote}>
          <span className={styles.securityBadge}>🔒 Secure</span>
          <span className={styles.securityText}>
            Your data is protected
          </span>
        </div>
      </div>
    </GenericModal>
  );
};

export default SessionTimeoutModal;
